/**
 * A cover image framed by its completeness gauge: a thin gold line that traces the frame
 * clockwise from the top-left corner, as far as the record is complete. A complete record is
 * fully framed. The gauge is real data (the record's weighted field coverage), never ornament.
 *
 * Covers are always 3:4, so the frame's perimeter is 3 + 4 + 3 + 4 units. Each edge is its own
 * segment filled to an exact fraction; the segments animate in sequence so the line draws
 * continuously around the corner.
 */
import { coverUrl } from '../lib/api'

const EDGES = [
  ['top', 3],
  ['right', 4],
  ['bottom', 3],
  ['left', 4],
] as const
const PERIMETER = 14

export function gaugeSegments(score: number) {
  let remaining = (Math.min(Math.max(score, 0), 100) / 100) * PERIMETER
  let start = 0
  return EDGES.map(([edge, length]) => {
    const fill = Math.min(Math.max(remaining / length, 0), 1)
    const seg = { edge, fill, delay: start / PERIMETER, duration: (length * fill) / PERIMETER }
    remaining -= length
    start += length
    return seg
  }).filter((s) => s.fill > 0)
}

interface Props {
  cover: string | null | undefined
  title: string
  /** 0-100. Omit to draw no gauge (e.g. publishers, which have no cover). */
  score?: number
  size?: 'sm' | 'md' | 'lg'
  /** Show the percentage permanently instead of on hover/focus. */
  showScore?: boolean
  /** Lazy-load below the fold; eager for the record page's hero cover. */
  eager?: boolean
  /** The cover is withheld on purpose (it would give the product away), so it is not a gap. */
  withheld?: boolean
  /** A one-page RPG: without a cover it gets a title card, labelled as such, rather than a gap. */
  onePage?: boolean
}

function blankNote(score: number | undefined, withheld?: boolean, onePage?: boolean): string {
  if (onePage) return 'One-page RPG'
  if (score === undefined) return ''
  return withheld ? 'Cover withheld' : 'No cover yet'
}

export function Cover({
  cover,
  title,
  score,
  size = 'md',
  showScore = false,
  eager = false,
  withheld,
  onePage,
}: Props) {
  const src = coverUrl(cover)
  const note = blankNote(score, withheld, onePage)
  return (
    <div className={`cover cover-${size}`} data-complete={score === 100 || undefined}>
      {src ? (
        <img
          src={src}
          alt=""
          width={300}
          height={400}
          loading={eager ? 'eager' : 'lazy'}
          fetchPriority={eager ? 'high' : 'auto'}
          decoding="async"
          draggable={false}
        />
      ) : (
        <div className={`cover-blank${onePage ? ' is-one-page' : ''}`} aria-hidden="true">
          <span>{title}</span>
          {note && <small>{note}</small>}
        </div>
      )}
      {score !== undefined && (
        <>
          <span className="gauge" aria-hidden="true">
            {gaugeSegments(score).map((g) => (
              <span
                key={g.edge}
                className={`gauge-seg gauge-${g.edge}`}
                style={{ '--fill': g.fill, '--delay': g.delay, '--dur': g.duration } as React.CSSProperties}
              />
            ))}
          </span>
          <span className={`gauge-score${showScore ? ' is-shown' : ''}`} aria-hidden="true">
            {score}%
          </span>
        </>
      )}
    </div>
  )
}
